import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Form, Input, Row, Col, Typography } from "antd";
import { getErrorProps } from "@utils/helpers";
import { ButtonComponent, InputText } from "@comps/components";
import { signinUser } from "../authSlice";
import {
  ArrowRightOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined
} from "@ant-design/icons";
import ForgetPassword from "./ForgetPassword";
import "./SignIn.css";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { authTooltips } from "../../common/authTooltips";

const fieldLabel = makeFieldLabel(authTooltips.auth, { namespace: "auth" });

const { Title, Text } = Typography;


let credentials = {};
const renderRequiredMark = (label, { required }) => (
  <>
    { label }
    { required && <span className="signInRequiredMark" aria-hidden="true">*</span> }
  </>
);

const SignIn = () => {
  const dispatch = useDispatch();
  const { loaderState, errors } = useSelector((state) => state.auth);
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [passwordInputFocused, setPasswordInputFocused] = useState(false);
  const [passwordLength, setPasswordLength] = useState(0);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [emailInputFocused, setEmailInputFocused] = useState(false);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    handleLoading(errors);
  }, [errors]);

  //  const getGeoLatLog=() =>{
  //     navigator.geolocation.getCurrentPosition(
  //       (position) => {
  //           console.log("Latitude:", position.coords.latitude);
  //           console.log("Longitude:", position.coords.longitude);
  //       },
  //       (error) => message.info(error.message),
  //       { enableHighAccuracy: true }
  //   );

  //   }
  const handleLoading = (errors) => {
    if (errors && errors.email !== undefined) {
      setLoading(false);
    }
    if (errors && errors.email === undefined && credentials.email !== undefined) {
      setLoading(true);
    }
    if (errors && errors.email === undefined && credentials.email === undefined) {
      setLoading(false);
    }
  };
  const showForgotPasswordModal = () => {
    setForgotPasswordVisible(true);
  };

  const closeForgotPasswordModal = () => {
    setForgotPasswordVisible(false);
  };

  const handleLoginSubmit = (data) => {
    credentials.email = data.email;
    credentials.password = data.password;
    credentials.remember_me = Boolean(data.remember);
    setLoading(true);
    dispatch(signinUser(credentials));
    credentials = {};
  };

  const handlePasswordInputFocus = () => {
    setPasswordInputFocused(true);
    setEmailInputFocused(true);
  };

  const handlePasswordInputBlur = () => {
    setPasswordInputFocused(false);
    setEmailInputFocused(false);
  };

  const checkPasswordLength = (password) => {
    const length = password.trim().length;
    setPasswordLength(length);
  };

  const checkEmailValidity = (email) => {
    const isValid = /@/.test(email);
    setIsEmailValid(isValid);
  };

  return (
    <div className="signInPage">
      <Row className="signInLayout">
        <Col xs={ { span: 0 } } lg={ { span: 13 } } className="signInShowcaseCol">
          <div className="signInShowcase">
            <div className="signInSheen" aria-hidden="true" />
            <div className="signInBrand">
              <img src="loginicon/image170.svg" alt="KLOK logo" />
              <div className="signInBrandText">
                <h1>HRMS</h1>
                <p>HR management that keeps operations clear, connected and easy to run.</p>
              </div>
            </div>

            <div className="signInFeaturePanel">
              <span className="signInBadge">Smart HR Workspace</span>
              <h2>
                Run your people<br />
                operations from one<br />
                <span>polished control center.</span>
              </h2>
              <p>
                Attendance, employee records, payroll and team visibility — all in a cleaner experience built for modern HR teams.
              </p>
            </div>

            <div className="signInHighlights">
              <span><ClockCircleOutlined />Attendance Tracking</span>
              <span><CreditCardOutlined />Payroll Simplified</span>
              <span><UserOutlined />Employee Records</span>
            </div>
          </div>
        </Col>

        <Col xs={ { span: 24 } } lg={ { span: 11 } } className="signInFormCol">
          <div className="signInFormShell">
            <div className="signInCard">
              {/* <div className="signInCardHeader">
                <img src="loginicon/image170.svg" alt="not found" />
                <img src="loginicon/KLOK.svg" alt="not found" />
              </div> */}

              <Title className="signInHeading">Welcome back</Title>
              <Text className="signInSubtitle">
                Sign in to continue to your HR workspace and pick up right where you left off.
              </Text>

              <Form
                layout="vertical"
                requiredMark={ renderRequiredMark }
                name="basic"
                initialValues={ { remember: true } }
                className="signInForm"
                onFinish={ handleLoginSubmit }
              >
                <Form.Item
                  name="email"
                  rules={ [
                    { required: true, message: "Please input your email!" },
                  ] }
                  label={ fieldLabel("email", "Username or Email") }
                  className="da-mb-16"
                  { ...getErrorProps(errors ? errors.email : "") }
                >
                  <InputText
                    placeholder="Enter username or email"
                    prefix={
                      <MailOutlined
                        className={ `mail-icon ${emailInputFocused ? "focused" : ""} ${isEmailValid ? "" : "error"}` }
                      />
                    }
                    onFocus={ handlePasswordInputFocus }
                    onBlur={ handlePasswordInputBlur }
                    onChange={ (e) => checkEmailValidity(e.target.value) }
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={ [
                    { required: true, message: "Please input your password!" },
                    { min: 6, message: "Minimum password length is 6" },
                    { max: 30, message: "Maximum password length is 30" },
                  ] }
                  label={ fieldLabel("password", "Password") }
                  className="da-mb-8"
                  { ...getErrorProps(errors ? errors.password : "") }
                >
                  <Input.Password
                    type="password"
                    placeholder="Enter your password"
                    prefix={
                      <LockOutlined
                        className={ `lock-icon ${passwordInputFocused ? "focused" : ""} ${passwordLength > 0 && passwordLength < 6 ? "error" : ""}` }
                      />
                    }
                    onFocus={ handlePasswordInputFocus }
                    onBlur={ handlePasswordInputBlur }
                    onChange={ (e) => checkPasswordLength(e.target.value) }
                  />
                </Form.Item>

                <div className="signInOptions">
                  <Form.Item name="remember" valuePropName="checked" className="da-mb-0">
                    <Checkbox>Remember me</Checkbox>
                  </Form.Item>

                  <button
                    type="button"
                    className="signInForgotLink"
                    onClick={ showForgotPasswordModal }
                  >
                    Forgot Password?
                  </button>
                </div>

                <Form.Item className="da-mt-16 da-mb-8">
                  <ButtonComponent className="actionButton" type="primary" htmlType="submit" state={ loaderState } loading={ loading }>
                    Log in <ArrowRightOutlined />
                  </ButtonComponent>
                </Form.Item>
              </Form>

              {/* <div className="signInDemoHint">
                Demo access — username <strong>dev</strong> · password <strong>123456</strong>
              </div>
              <div className="signInSetupHint">
                New organization? <span>Set up KLOK</span>
              </div> */}

              <ForgetPassword
                visible={ forgotPasswordVisible }
                onCancel={ closeForgotPasswordModal }
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SignIn;
